import { Event, Button, Component, EventTarget, Node, NodeEventType, warn, _decorator } from 'cc';
import { DEV } from 'cc/env';

const { ccclass, property } = _decorator;

enum EventType {
    PAGE_INITED = 'PAGE_INITED',
    OPEN_START = 'OPEN_START',
    OPEN_END = 'OPEN_END',
    CLOSE_START = 'CLOSE_START',
    CLOSE_END = 'CLOSE_END',
    IS_ANY_PAGE_VISIBLE_CHANGED = 'IS_ANY_PAGE_VISIBLE_CHANGED',
    VISIBLE_PAGE_COUNT_CHANGED = 'VISIBLE_PAGE_COUNT_CHANGED',
}

const blockEvents = [
    NodeEventType.TOUCH_START,
    NodeEventType.TOUCH_MOVE,
    NodeEventType.TOUCH_END,
    NodeEventType.TOUCH_CANCEL,
    NodeEventType.MOUSE_DOWN,
    NodeEventType.MOUSE_MOVE,
    NodeEventType.MOUSE_UP,
    NodeEventType.MOUSE_ENTER,
    NodeEventType.MOUSE_LEAVE,
    NodeEventType.MOUSE_WHEEL,
];

@ccclass('Page')
export default class Page extends Component {
    public static readonly EVENT_TYPE = EventType;
    public static readonly EVENT_TARGET = new EventTarget();
    public static readonly INSTANCES: Page[] = [];
    private static _visiblePageCount = 0;
    private static _isAnyPageVisible = false;

    @property(Button)
    public readonly openButton: Button = null;

    @property(Button)
    public readonly closeButton: Button = null;

    @property
    public closeOnBlur: boolean = false;

    @property({
        tooltip: DEV && '是否允許在開啟動畫尚未結束前就關閉彈窗',
    })
    public useForceClose: boolean = false;

    @property({
        tooltip: DEV && '是否允許在關閉動畫尚未結束前就開啟彈窗',
    })
    public useForceOpen: boolean = false;

    @property({
        tooltip: DEV && '是否在開始關閉彈窗時立刻將遮罩關閉，通常在 useForceOpen 為 true 時使用，以避免事件在關閉動畫過程中被遮罩遮擋',
    })
    public closeMaskImmediately: boolean = false;

    @property
    public blockEvents: boolean = true;

    @property({ type: Node, formerlySerializedAs: 'view', visible: true })
    protected _view: Node = null;

    @property({ type: Node, formerlySerializedAs: 'mask', visible: true })
    protected _mask: Node = null;

    @property({ tooltip: DEV && '是否加入實例隊列進行管理，若加入將會影響 Page.isAnyPageVisible 和 Page.visiblePageCount 的計算' })
    private readonly addToInstances: boolean = true;

    private _addtionalOpenEffects: ((playTransition: boolean) => Promise<void>)[] = [];
    private _addtionalCloseEffects: ((playTransition: boolean) => Promise<void>)[] = [];
    private _isClosing = false;
    private _isOpening = false;
    private _isMoving = false;
    private _actionID = -1;

    public static get isAnyPageVisible() {
        return this._isAnyPageVisible;
    }
    private static set isAnyPageVisible(value: boolean) {
        if (this._isAnyPageVisible === value) {
            return;
        }
        this._isAnyPageVisible = value;
        this.EVENT_TARGET.emit(EventType.IS_ANY_PAGE_VISIBLE_CHANGED, value);
    }

    public static get visiblePageCount() {
        return this._visiblePageCount;
    }
    private static set visiblePageCount(value: number) {
        if (value < 0) {
            warn('visiblePageCount should not be less than 0');
            value = 0;
        }
        if (this._visiblePageCount === value) {
            return;
        }
        this._visiblePageCount = value;
        this.isAnyPageVisible = value > 0;
        this.EVENT_TARGET.emit(EventType.VISIBLE_PAGE_COUNT_CHANGED, value);
    }

    public get view() {
        return this._view;
    }
    protected set view(value: Node) {
        this._view = value;
    }
    public get mask() {
        return this._mask;
    }
    protected set mask(value: Node) {
        this._mask = value;
    }

    public get isVisible() {
        return this.view.active;
    }

    public get isMoving() {
        return this._isMoving;
    }
    private set isMoving(value: boolean) {
        if (value === this._isMoving) {
            return;
        }
        this._isMoving = value;
        this._onIsMovingChanged(value);
    }

    public get isOpening() {
        return this._isOpening;
    }
    private set isOpening(value: boolean) {
        if (value === this._isOpening) {
            return;
        }
        this._isOpening = value;
        this._updateIsMoving();
        this.node.emit(value ? EventType.OPEN_START : EventType.OPEN_END);
    }

    public get isClosing() {
        return this._isClosing;
    }
    private set isClosing(value: boolean) {
        if (value === this._isClosing) {
            return;
        }
        this._isClosing = value;
        this._updateIsMoving();
        this.node.emit(value ? EventType.CLOSE_START : EventType.CLOSE_END);
    }

    public onLoad() {
        this.on(Node.EventType.SIZE_CHANGED, this._ensureNodeSizeZero, this);
        this._ensureNodeSizeZero();
        if (this.addToInstances) {
            Page.INSTANCES.push(this);
            // handle situation when page is visible by default after page is instantiated
            if (this.isVisible) {
                Page.visiblePageCount++;
            }
        }

        this.openButton?.node.on(Button.EventType.CLICK, () => {
            this.open(this.useForceOpen);
        });
        this.closeButton?.node.on(Button.EventType.CLICK, () => {
            this.close(this.useForceClose);
        });
        this.mask?.on(Node.EventType.TOUCH_END, () => {
            if (this.closeOnBlur) {
                this.close(this.useForceClose);
            }
        });

        this.afterOnLoad();
    }

    public onDestroy() {
        this.off(Node.EventType.SIZE_CHANGED, this._ensureNodeSizeZero, this);
        if (this.addToInstances) {
            const index = Page.INSTANCES.findIndex((page) => page === this);
            Page.INSTANCES.splice(index, 1);
            // handle situation when page is destroyed while it is still visible
            if (this.isVisible) {
                Page.visiblePageCount -= 1;
            }
        }
    }

    public async open(isForce = false, playTransition = true) {
        // check if action is valid
        if (this.isOpening) {
            return;
        }
        if (this.isVisible && !this._isClosing) {
            return;
        }
        if (this.isClosing) {
            if (isForce) {
                this._isClosing = false;
            } else {
                return;
            }
        }

        const actionID = ++this._actionID;
        if (this.mask) {
            this.mask.active = true;
        }
        if (!this.view.active) {
            this.view.active = true;
            if (this.addToInstances) {
                Page.visiblePageCount++;
            }
        }
        this.isOpening = true;
        await Promise.all([this.playOpen(playTransition), ...this._addtionalOpenEffects.map((effect) => effect(playTransition))]);
        if (this._actionID === actionID) {
            // if actionID is different, it means open or close action has been triggered during the opening process, so we should not set isOpening to false
            this.isOpening = false;
        }
    }

    public async close(isForce = false, playTransition = true) {
        // check if action is valid
        if (this.isClosing) {
            return;
        }
        if (!this.isVisible) {
            return;
        }
        if (this.isOpening) {
            if (isForce) {
                this._isOpening = false;
            } else {
                return;
            }
        }

        const actionID = ++this._actionID;
        this.isClosing = true;
        if (this.closeMaskImmediately && this.mask) {
            this.mask.active = false;
        }
        await Promise.all([this.playClose(playTransition), ...this._addtionalCloseEffects.map((effect) => effect(playTransition))]);
        if (this._actionID === actionID) {
            // if actionID is different, it means open or close action has been triggered during the closing process, so we should not set view to inactive
            if (this.mask) {
                this.mask.active = false;
            }
            this.view.active = false;
            if (this.addToInstances) {
                Page.visiblePageCount--;
            }
            this.isClosing = false;
        }
    }

    public addAddtionalOpenEffect(effect: (playTransition: boolean) => Promise<void>) {
        if (!effect) {
            return;
        }
        this._addtionalOpenEffects.push(effect);
    }

    public addAddtionalCloseEffect(effect: (playTransition: boolean) => Promise<void>) {
        if (!effect) {
            return;
        }
        this._addtionalCloseEffects.push(effect);
    }

    public removeAddtionalOpenEffect(effect: (playTransition: boolean) => Promise<void>) {
        const index = this._addtionalOpenEffects.findIndex((item) => item === effect);
        if (index !== -1) {
            this._addtionalOpenEffects.splice(index, 1);
        }
    }

    public removeAddtionalCloseEffect(effect: (playTransition: boolean) => Promise<void>) {
        const index = this._addtionalCloseEffects.findIndex((item) => item === effect);
        if (index !== -1) {
            this._addtionalCloseEffects.splice(index, 1);
        }
    }

    public waitForOpen() {
        return new Promise((resolve) => {
            this.node.once(Page.EVENT_TYPE.OPEN_START, resolve);
        });
    }

    public waitForClose() {
        return new Promise((resolve) => {
            this.node.once(Page.EVENT_TYPE.CLOSE_END, resolve);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected playOpen(_playTransition: boolean): Promise<void> {
        return Promise.resolve();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected playClose(_playTransition: boolean): Promise<void> {
        return Promise.resolve();
    }

    protected afterOnLoad() {
        this.node.emit(Page.EVENT_TYPE.PAGE_INITED);
    }

    private _updateIsMoving() {
        this.isMoving = this._isOpening || this._isClosing;
    }

    private _onIsMovingChanged(isMoving: boolean) {
        if (!this.blockEvents) {
            return;
        }
        if (isMoving) {
            blockEvents.forEach((blockEvent) => this.node.on(blockEvent, this._blockEvent, this, true));
        } else {
            blockEvents.forEach((blockEvent) => this.node.off(blockEvent, this._blockEvent, this, true));
        }
    }

    private _blockEvent(event: Event) {
        if (this.isOpening && this.useForceClose && (event.target === this.closeButton?.node || (this.closeOnBlur && event.target === this.mask))) {
            return;
        }
        // block all input event when page is moving
        event.propagationStopped = true;
    }

    private _ensureNodeSizeZero() {
        this.node.transform.setContentSize(0, 0);
    }
}
